import { useEffect, useRef, useState } from 'react';
import { INVITE_TTL_OPTIONS, MAX_ACTIVE_INVITES, MAX_LISTENERS, type AdminState, type InviteSummary, type InviteTtlSeconds, type OwnerSession, type ScheduleBroadcastType, type ScheduleSuggestions, type StreamView } from '@ai-voice/contracts';
import { ApiError, api, clearOwnerSession, saveOwnerSession } from '../api';
import { captureSharedAudio, enterHostRoom, isDesktopRuntime, isSystemAudioCaptureSupported, openDesktopTabMode, type CaptureMode, type SystemAudioCapture, type TrtcClient } from '../trtc';
import { getLanguage, getLanguageTimeZone, t, tError } from '../i18n';
import { selectableMatches } from '../match-window';
import { useScheduleNameLocalizer } from '../schedule-i18n';

type Props = {
  session: OwnerSession;
  onLogout: () => void;
};

type HostController = Awaited<ReturnType<typeof enterHostRoom>>;
type OwnedStreamRuntime = {
  sessionId: string;
  roomId: string;
  matchId: string;
  capture: SystemAudioCapture | null;
  controller: HostController | null;
  needsCapture: boolean;
  stopCaptureWatcher?: () => void;
};
type OwnedStreamView = StreamView & { needsCapture: boolean };
type StoredOwnedStream = Pick<OwnedStreamRuntime, 'sessionId' | 'roomId' | 'matchId'>;
const OWNER_STREAMS_KEY = 'ai-voice-owner-streams';

function watchCaptureEnded(capture: SystemAudioCapture, onEnded: () => void): () => void {
  let handled = false;
  const handleEnded = () => {
    if (handled) return;
    handled = true;
    onEnded();
  };
  const tracks = [capture.track];
  for (const track of tracks) {
    if (track.readyState === 'ended') {
      window.queueMicrotask(handleEnded);
    } else {
      track.addEventListener('ended', handleEnded, { once: true });
    }
  }
  return () => {
    handled = true;
    for (const track of tracks) track.removeEventListener('ended', handleEnded);
  };
}

function readStoredOwnedStreams(): StoredOwnedStream[] {
  try {
    const value = JSON.parse(sessionStorage.getItem(OWNER_STREAMS_KEY) ?? '[]') as StoredOwnedStream[];
    return Array.isArray(value)
      ? value.filter((item) => item.sessionId && item.roomId && item.matchId)
      : [];
  } catch {
    sessionStorage.removeItem(OWNER_STREAMS_KEY);
    return [];
  }
}

type AuditView = Awaited<ReturnType<typeof api.auditEvents>>[number];
type BanView = Awaited<ReturnType<typeof api.listBans>>[number];
type GeneratedInvite = Awaited<ReturnType<typeof api.createInvite>> & { status: InviteSummary['status'] };
type ScheduleView = Awaited<ReturnType<typeof api.adminSchedule>>[number];
type ActivationOrderView = Awaited<ReturnType<typeof api.adminActivationOrders>>[number];
type ActivationPaymentEventView = Awaited<ReturnType<typeof api.adminActivationPaymentEvents>>[number];

export function OwnerDashboard({ session: initialSession, onLogout }: Props) {
  const [session, setSession] = useState(initialSession);
  const [state, setState] = useState<AdminState | null>(null);
  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [activationCodes, setActivationCodes] = useState<InviteSummary[]>([]);
  const [activationCodeSource, setActivationCodeSource] = useState<'owner' | 'purchase' | 'free'>('owner');
  const [activationCodeQuery, setActivationCodeQuery] = useState('');
  const [scheduleMatches, setScheduleMatches] = useState<ScheduleView[]>([]);
  const [scheduleForm, setScheduleForm] = useState({ id: '', startsAt: '', league: '', homeTeam: '', awayTeam: '', broadcastType: 'foreign' as ScheduleBroadcastType });
  const [scheduleSuggestions, setScheduleSuggestions] = useState<ScheduleSuggestions>({ leagues: [], teams: [] });
  const [generatedInvites, setGeneratedInvites] = useState<GeneratedInvite[]>([]);
  const [inviteTtlSeconds, setInviteTtlSeconds] = useState<InviteTtlSeconds>(86_400);
  const [bans, setBans] = useState<BanView[]>([]);
  const [audit, setAudit] = useState<AuditView[]>([]);
  const [activationOrders, setActivationOrders] = useState<ActivationOrderView[]>([]);
  const [activationPaymentEvents, setActivationPaymentEvents] = useState<ActivationPaymentEventView[]>([]);
  const [sharing, setSharing] = useState(false);
  const [captureMode, setCaptureMode] = useState<CaptureMode>('system');
  const [selectedMatchId, setSelectedMatchId] = useState('');
  const [ownedStreams, setOwnedStreams] = useState<OwnedStreamView[]>([]);
  const [captureState, setCaptureState] = useState<'idle' | 'requesting' | 'live' | 'error'>('idle');
  const [connectionState, setConnectionState] = useState<'connected' | 'connecting' | 'disconnected'>('disconnected');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [usageLimit, setUsageLimit] = useState(9000);
  const [virtualOnlineBase, setVirtualOnlineBase] = useState(90);
  const [scrollingMessage, setScrollingMessage] = useState('');
  const [paidActivationEnabled, setPaidActivationEnabled] = useState(true);
  const [activationPriceDay, setActivationPriceDay] = useState(10);
  const [activationPriceWeek, setActivationPriceWeek] = useState(50);
  const [activationPriceMonth, setActivationPriceMonth] = useState(100);
  const [freeActivationEnabled, setFreeActivationEnabled] = useState(true);
  const [freeActivationStartDate, setFreeActivationStartDate] = useState('2026-09-01');
  const [freeActivationEndDate, setFreeActivationEndDate] = useState('2026-09-30');
  const [showAudit, setShowAudit] = useState(false);
  const [clockMs, setClockMs] = useState(Date.now());
  const localizeScheduleName = useScheduleNameLocalizer();
  const sessionRef = useRef(session);
  const hostRef = useRef<HostController | null>(null);
  const captureRef = useRef<SystemAudioCapture | null>(null);
  const ownedStreamsRef = useRef<OwnedStreamRuntime[]>([]);
  const usageDirtyRef = useRef(false);
  const virtualOnlineDirtyRef = useRef(false);
  const scrollingMessageDirtyRef = useRef(false);
  const activationPricesDirtyRef = useRef(false);
  const paidActivationDirtyRef = useRef(false);
  const freeActivationDirtyRef = useRef(false);
  const sharingRef = useRef(false);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    sharingRef.current = sharing;
  }, [sharing]);

  async function withAuth<T>(action: (accessToken: string) => Promise<T>): Promise<T> {
    try {
      return await action(sessionRef.current.accessToken);
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.code !== 'UNAUTHORIZED') throw cause;
      const refreshed = await api.refresh(sessionRef.current.refreshToken);
      saveOwnerSession(refreshed);
      sessionRef.current = refreshed;
      setSession(refreshed);
      return action(refreshed.accessToken);
    }
  }

  async function loadState(silent = false) {
    try {
      const [nextState, nextSchedule, nextSuggestions, nextOrders, nextPaymentEvents, nextActivationCodes] = await Promise.all([
        withAuth((token) => api.adminState(token)),
        withAuth((token) => api.adminSchedule(token)),
        withAuth((token) => api.scheduleSuggestions(token)),
        withAuth((token) => api.adminActivationOrders(token)),
        withAuth((token) => api.adminActivationPaymentEvents(token)),
        withAuth((token) => api.activationCodes(token))
      ]);
      setState(nextState);
      setScheduleMatches(nextSchedule);
      setScheduleSuggestions(nextSuggestions);
      const liveStreamIds = new Set(nextState.streams.map((stream) => stream.sessionId));
      const retained = ownedStreamsRef.current.filter((stream) => liveStreamIds.has(stream.sessionId));
      if (retained.length !== ownedStreamsRef.current.length) {
        ownedStreamsRef.current = retained;
        syncOwnedStreams();
      }
      setActivationOrders(nextOrders);
      setActivationPaymentEvents(nextPaymentEvents);
      setActivationCodes(nextActivationCodes);
      if (!nextState.streamActive && sharingRef.current) {
        captureRef.current?.stop();
        void hostRef.current?.stop();
        captureRef.current = null;
        hostRef.current = null;
        sharingRef.current = false;
        setSharing(false);
        setCaptureState('idle');
        setConnectionState('disconnected');
      }
      setInvites(nextState.invites);
      setGeneratedInvites((current) => current.map((invite) => {
        const summary = nextState.invites.find((item) => item.inviteId === invite.inviteId);
        return summary ? { ...invite, status: summary.status } : invite;
      }));
      if (!usageDirtyRef.current) setUsageLimit(nextState.usage.monthlyLimitMinutes);
      if (!virtualOnlineDirtyRef.current) setVirtualOnlineBase(nextState.displaySettings.virtualOnlineBase);
      if (!scrollingMessageDirtyRef.current) setScrollingMessage(nextState.displaySettings.scrollingMessage);
      if (!activationPricesDirtyRef.current) {
        setActivationPriceDay(nextState.displaySettings.activationPriceDay);
        setActivationPriceWeek(nextState.displaySettings.activationPriceWeek);
        setActivationPriceMonth(nextState.displaySettings.activationPriceMonth);
      }
      if (!paidActivationDirtyRef.current) setPaidActivationEnabled(nextState.displaySettings.paidActivationEnabled);
      if (!freeActivationDirtyRef.current) {
        setFreeActivationEnabled(nextState.displaySettings.freeActivationEnabled);
        setFreeActivationStartDate(nextState.displaySettings.freeActivationStartDate);
        setFreeActivationEndDate(nextState.displaySettings.freeActivationEndDate);
      }
      if (!silent) {
        const [nextBans, nextAudit] = await Promise.all([
          withAuth((token) => api.listBans(token)),
          withAuth((token) => api.auditEvents(token))
        ]);
        setBans(nextBans);
        setAudit(nextAudit);
        setError('');
      }
    } catch (cause) {
      if (!silent) setError(messageOf(cause));
    }
  }

  useEffect(() => {
    void loadState();
    const timer = window.setInterval(() => void loadState(true), 3000);
  return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!generatedInvites.some((invite) => invite.status === 'active') && !invites.some((invite) => invite.status === 'active') && !activationCodes.length && !scheduleMatches.length) return;
    setClockMs(Date.now());
    const timer = window.setInterval(() => setClockMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [generatedInvites, invites, activationCodes, scheduleMatches]);

  useEffect(() => {
    if (!sharing && ownedStreams.length === 0) return;
    const timer = window.setInterval(() => {
      if (sharingRef.current) void withAuth((token) => api.streamHeartbeat(token)).catch(() => undefined);
      for (const stream of ownedStreamsRef.current) {
        void withAuth((token) => api.streamHeartbeat(token, stream.sessionId)).catch(() => undefined);
      }
    }, 10_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [sharing, ownedStreams.length]);

  const selectableScheduleMatches = selectableMatches(scheduleMatches, clockMs);

  useEffect(() => {
    if (selectableScheduleMatches.some((match) => match.id === selectedMatchId)) return;
    setSelectedMatchId(selectableScheduleMatches[0]?.id ?? '');
  }, [scheduleMatches, selectedMatchId, clockMs]);

  useEffect(() => {
    const restored = readStoredOwnedStreams();
    if (!restored.length) return;
    ownedStreamsRef.current = restored.map((stream) => ({ ...stream, capture: null, controller: null, needsCapture: true }));
    setOwnedStreams(restored.map((stream) => ({
      sessionId: stream.sessionId,
      roomId: stream.roomId,
      status: 'active',
      matchId: stream.matchId,
      captureMode: 'tab',
      startedAt: new Date().toISOString(),
      listenerCount: 0,
      needsCapture: true
    })));
  }, []);

  useEffect(() => {
    sessionStorage.setItem(OWNER_STREAMS_KEY, JSON.stringify(ownedStreamsRef.current.map((stream) => ({
      sessionId: stream.sessionId,
      roomId: stream.roomId,
      matchId: stream.matchId
    }))));
  }, [ownedStreams]);
  useEffect(() => () => {
    captureRef.current?.stop();
    void hostRef.current?.stop();
    for (const stream of ownedStreamsRef.current) {
      stream.stopCaptureWatcher?.();
      stream.capture?.stop();
      void stream.controller?.stop().catch(() => undefined);
    }
    ownedStreamsRef.current = [];
  }, []);

  function syncOwnedStreams(): void {
    setOwnedStreams(ownedStreamsRef.current.map((stream) => {
      const match = scheduleMatches.find((item) => item.id === stream.matchId);
      return {
        sessionId: stream.sessionId,
        roomId: stream.roomId,
        status: 'active',
        matchId: stream.matchId,
        captureMode: 'tab',
        startedAt: new Date().toISOString(),
        listenerCount: 0,
        match,
        needsCapture: stream.needsCapture
      };
    }));
  }

  async function startTabStream(): Promise<void> {
    if (!selectedMatchId) {
      setError(t('请先选择比赛。'));
      return;
    }
    if (isDesktopRuntime()) {
      await openDesktopTabMode();
      setError('');
      setCaptureState('idle');
      setNotice(t('已在网页版打开主控页面。标签页独立直播请在外部浏览器中完成，请勿关闭浏览器窗口。'));
      return;
    }
    setBusy('start');
    setError('');
    setCaptureState('requesting');
    let capture: SystemAudioCapture | null = null;
    let createdSessionId = '';
    try {
      capture = await captureSharedAudio('tab');
      const credentials = await withAuth((token) => api.startMatchStream(token, selectedMatchId, 'tab'));
      createdSessionId = credentials.sessionId;
      const controller = await enterHostRoom({
        ...credentials,
        audioTrack: capture.track,
        onConnectionState: () => undefined,
        onNetworkQuality: () => undefined
      });
      const runtime: OwnedStreamRuntime = {
        sessionId: credentials.sessionId,
        roomId: credentials.roomId,
        matchId: selectedMatchId,
        capture,
        controller,
        needsCapture: false
      };
      runtime.stopCaptureWatcher = watchCaptureEnded(capture, () => void stopOwnedStream(credentials.sessionId));
      ownedStreamsRef.current = [...ownedStreamsRef.current, runtime];
      syncOwnedStreams();
      setCaptureState('live');
      setConnectionState('connected');
      await loadState(true);
    } catch (cause) {
      capture?.stop();
      if (createdSessionId) await withAuth((token) => api.stopMatchStream(token, createdSessionId)).catch(() => undefined);
      setCaptureState('error');
      setError(messageOf(cause));
    } finally {
      setBusy('');
    }
  }

  async function restoreOwnedCapture(sessionId: string): Promise<void> {
    const stream = ownedStreamsRef.current.find((item) => item.sessionId === sessionId);
    if (!stream || !stream.needsCapture) return;
    setBusy(`restore:${sessionId}`);
    setError('');
    let capture: SystemAudioCapture | null = null;
    let rejoined = false;
    try {
      capture = await captureSharedAudio('tab');
      const credentials = await withAuth((token) => api.rejoinMatchStream(token, sessionId));
      rejoined = true;
      const controller = await enterHostRoom({
        ...credentials,
        audioTrack: capture.track,
        onConnectionState: () => undefined,
        onNetworkQuality: () => undefined
      });
      stream.stopCaptureWatcher?.();
      stream.capture = capture;
      stream.controller = controller;
      stream.needsCapture = false;
      stream.stopCaptureWatcher = watchCaptureEnded(capture, () => void stopOwnedStream(sessionId));
      syncOwnedStreams();
      setCaptureState('live');
      setConnectionState('connected');
    } catch (cause) {
      capture?.stop();
      if (rejoined) {
        await withAuth((token) => api.stopMatchStream(token, sessionId)).catch(() => undefined);
        ownedStreamsRef.current = ownedStreamsRef.current.filter((item) => item.sessionId !== sessionId);
        syncOwnedStreams();
      }
      setError(messageOf(cause));
    } finally {
      setBusy('');
    }
  }
  async function stopOwnedStream(sessionId: string): Promise<void> {
    const stream = ownedStreamsRef.current.find((item) => item.sessionId === sessionId);
    if (!stream) return;
    setBusy(`stop:${sessionId}`);
    try {
      await withAuth((token) => api.stopMatchStream(token, sessionId));
    } finally {
      stream.stopCaptureWatcher?.();
      await stream.controller?.stop().catch(() => undefined);
      stream.capture?.stop();
      ownedStreamsRef.current = ownedStreamsRef.current.filter((item) => item.sessionId !== sessionId);
      syncOwnedStreams();
      if (!ownedStreamsRef.current.length && !sharingRef.current) setCaptureState('idle');
      await loadState(true);
      setBusy('');
    }
  }
  async function startSharing() {
    if (captureMode === 'tab') {
      await startTabStream();
      return;
    }
    setBusy('start');
    setError('');
    setCaptureState('requesting');
    try {
      const capture = await captureSharedAudio(captureMode);
      captureRef.current = capture;
      setCaptureState('live');
      const credentials = await withAuth((token) => api.startStream(token));
      const controller = await enterHostRoom({
        ...credentials,
        audioTrack: capture.track,
        onConnectionState: setConnectionState,
        onNetworkQuality: () => undefined
      });
      hostRef.current = controller;
      setSharing(true);
      setConnectionState('connected');
      usageDirtyRef.current = false;
      await loadState(true);
    } catch (cause) {
      captureRef.current?.stop();
      captureRef.current = null;
      setCaptureState('error');
      setError(messageOf(cause));
      try { await withAuth((token) => api.stopStream(token)); } catch { /* Best effort cleanup. */ }
    } finally {
      setBusy('');
    }
  }

  async function stopSharing() {
    setBusy('stop');
    setError('');
    try {
      if (ownedStreamsRef.current.length) {
        await withAuth((token) => api.stopAllStreams(token));
      }
      if (sharingRef.current) {
        await withAuth((token) => api.stopStream(token));
      }
    } finally {
      for (const stream of ownedStreamsRef.current) {
        stream.stopCaptureWatcher?.();
        await stream.controller?.stop().catch(() => undefined);
        stream.capture?.stop();
      }
      ownedStreamsRef.current = [];
      setOwnedStreams([]);
      await hostRef.current?.stop().catch(() => undefined);
      hostRef.current = null;
      captureRef.current?.stop();
      captureRef.current = null;
      setSharing(false);
      setCaptureState('idle');
      setConnectionState('disconnected');
      usageDirtyRef.current = false;
      await loadState(true);
      setBusy('');
    }
  }

  async function createInvite() {
    setBusy('invite');
    setError('');
    try {
      const invite = await withAuth((token) => api.createInvite(token, inviteTtlSeconds));
      setGeneratedInvites((current) => [{ ...invite, status: 'active' }, ...current]);
      usageDirtyRef.current = false;
      await loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy('');
    }
  }

  async function revokeInvite(inviteId: string) {
    try {
      await withAuth((token) => api.revokeInvite(token, inviteId));
      setGeneratedInvites((current) => current.map((invite) => invite.inviteId === inviteId ? { ...invite, status: 'revoked' } : invite));
      usageDirtyRef.current = false;
      await loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    }
  }

  async function participantAction(sessionId: string, action: 'pause' | 'resume' | 'kick' | 'ban') {
    setBusy(`${action}:${sessionId}`);
    setError('');
    try {
      const participant = state?.participants.find((item) => item.sessionId === sessionId);
      const reason = action === 'ban' ? window.prompt(t('封禁原因'), t('管理员封禁')) ?? undefined : undefined;
      await withAuth((token) => api.participantAction(token, sessionId, action, reason));
      if (participant && hostRef.current && (action === 'pause' || action === 'resume')) {
        hostRef.current.sendTargetCommand(participant.userId, action);
      }
      usageDirtyRef.current = false;
      await loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy('');
    }
  }

  async function unban(deviceId: string) {
    try {
      await withAuth((token) => api.unban(token, deviceId));
      usageDirtyRef.current = false;
      await loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    }
  }

  async function saveUsageLimit() {
    setBusy('usage');
    try {
      await withAuth((token) => api.updateUsageLimit(token, usageLimit));
      usageDirtyRef.current = false;
      await loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy('');
    }
  }

  async function saveVirtualOnlineBase() {
    setBusy('virtual-online');
    setError('');
    setNotice('');
    try {
      const updated = await withAuth((token) => api.updateDisplaySettings(token, { virtualOnlineBase }));
      setState((current) => current ? { ...current, displaySettings: updated } : current);
      setVirtualOnlineBase(updated.virtualOnlineBase);
      virtualOnlineDirtyRef.current = false;
      setNotice(t('虚拟在线人数已保存'));
      void loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy('');
    }
  }

  async function saveScrollingMessage(nextMessage = scrollingMessage) {
    setBusy('scrolling-message');
    setError('');
    setNotice('');
    try {
      const updated = await withAuth((token) => api.updateDisplaySettings(token, { scrollingMessage: nextMessage }));
      setState((current) => current ? { ...current, displaySettings: updated } : current);
      setScrollingMessage(updated.scrollingMessage);
      scrollingMessageDirtyRef.current = false;
      setNotice(updated.scrollingMessage ? t('滚动字幕已保存') : t('滚动字幕已清空'));
      void loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy('');
    }
  }

  async function saveActivationPrices() {
    setBusy('activation-prices');
    setError('');
    setNotice('');
    try {
      const updated = await withAuth((token) => api.updateDisplaySettings(token, {
        activationPriceDay,
        activationPriceWeek,
        activationPriceMonth
      }));
      setState((current) => current ? { ...current, displaySettings: updated } : current);
      setActivationPriceDay(updated.activationPriceDay);
      setActivationPriceWeek(updated.activationPriceWeek);
      setActivationPriceMonth(updated.activationPriceMonth);
      activationPricesDirtyRef.current = false;
      setNotice(t('激活码价格已保存'));
      void loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy('');
    }
  }

  async function togglePaidActivation() {
    const nextEnabled = !paidActivationEnabled;
    setBusy('paid-activation');
    paidActivationDirtyRef.current = true;
    setError('');
    setNotice('');
    try {
      const updated = await withAuth((token) => api.updateDisplaySettings(token, { paidActivationEnabled: nextEnabled }));
      setState((current) => current ? { ...current, displaySettings: updated } : current);
      setPaidActivationEnabled(updated.paidActivationEnabled);
      setNotice(updated.paidActivationEnabled ? t('收费模式已开启') : t('收费模式已关闭'));
      void loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      paidActivationDirtyRef.current = false;
      setBusy('');
    }
  }

  async function saveFreeActivationSchedule() {
    setBusy('free-activation-dates');
    setError('');
    setNotice('');
    try {
      const updated = await withAuth((token) => api.updateDisplaySettings(token, {
        freeActivationStartDate,
        freeActivationEndDate
      }));
      setState((current) => current ? { ...current, displaySettings: updated } : current);
      setFreeActivationStartDate(updated.freeActivationStartDate);
      setFreeActivationEndDate(updated.freeActivationEndDate);
      freeActivationDirtyRef.current = false;
      setNotice(t('免费活动时间已保存'));
      void loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy('');
    }
  }

  async function toggleFreeActivation() {
    const nextEnabled = !freeActivationEnabled;
    setBusy('free-activation-mode');
    freeActivationDirtyRef.current = true;
    setError('');
    setNotice('');
    try {
      const updated = await withAuth((token) => api.updateDisplaySettings(token, { freeActivationEnabled: nextEnabled }));
      setState((current) => current ? { ...current, displaySettings: updated } : current);
      setFreeActivationEnabled(updated.freeActivationEnabled);
      setNotice(updated.freeActivationEnabled ? t('免费活动已开启') : t('免费活动已关闭'));
      void loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      freeActivationDirtyRef.current = false;
      setBusy('');
    }
  }

  function resetScheduleForm(): void {
    setScheduleForm({ id: '', startsAt: '', league: '', homeTeam: '', awayTeam: '', broadcastType: 'foreign' });
  }

  function editSchedule(match: ScheduleView): void {
    setScheduleForm({
      id: match.id,
      startsAt: toDateTimeLocal(match.startsAt),
      league: match.league,
      homeTeam: match.homeTeam,
      awayTeam: match.awayTeam,
      broadcastType: match.broadcastType
    });
  }

  async function saveSchedule(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!scheduleForm.startsAt || !scheduleForm.league.trim() || !scheduleForm.homeTeam.trim() || !scheduleForm.awayTeam.trim()) {
      setError(t('请完整填写比赛时间、联赛、主队和客队'));
      return;
    }
    setBusy('schedule');
    setError('');
    try {
      const input = {
        startsAt: new Date(scheduleForm.startsAt).toISOString(),
        league: scheduleForm.league.trim(),
        homeTeam: scheduleForm.homeTeam.trim(),
        awayTeam: scheduleForm.awayTeam.trim(),
        broadcastType: scheduleForm.broadcastType
      };
      if (scheduleForm.id) await withAuth((token) => api.updateScheduleMatch(token, scheduleForm.id, input));
      else await withAuth((token) => api.createScheduleMatch(token, input));
      resetScheduleForm();
      await loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy('');
    }
  }

  async function deleteSchedule(id: string): Promise<void> {
    if (!window.confirm(t('确定删除这场赛程吗？'))) return;
    setBusy(`schedule-delete:${id}`);
    setError('');
    try {
      await withAuth((token) => api.deleteScheduleMatch(token, id));
      if (scheduleForm.id === id) resetScheduleForm();
      await loadState(true);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy('');
    }
  }
  function logout() {
    captureRef.current?.stop();
    void hostRef.current?.stop();
    clearOwnerSession();
    onLogout();
  }

  const captureSupported = isSystemAudioCaptureSupported();
  const captureModeLabel = captureMode === 'system' ? t('电脑系统音频') : t('Chrome 标签页音频');
  const anySharing = sharing || ownedStreams.length > 0;
  const activeInviteCount = state?.activeInviteCount ?? invites.filter((invite) => invite.status === 'active').length;
  const activationCodeCounts = {
    owner: activationCodes.filter((invite) => invite.source === 'owner').length,
    purchase: activationCodes.filter((invite) => invite.source === 'purchase').length,
    free: activationCodes.filter((invite) => invite.source === 'free').length
  };
  const normalizedActivationCodeQuery = activationCodeQuery.normalize('NFKC').trim().toLowerCase();
  const managedActivationCodes = activationCodes
    .filter((invite) => invite.source === activationCodeSource)
    .filter((invite) => !normalizedActivationCodeQuery || [
      invite.code,
      invite.nickname,
      invite.deviceIdShort,
      invite.activeDeviceIdShort
    ].filter(Boolean).some((value) => String(value).normalize('NFKC').toLowerCase().includes(normalizedActivationCodeQuery)));

  const usagePercent = state ? Math.min(100, Math.round((state.usage.monthParticipantMinutes / Math.max(1, state.usage.monthlyLimitMinutes)) * 100)) : 0;
  const pricesValid = [activationPriceDay, activationPriceWeek, activationPriceMonth]
    .every((price) => Number.isInteger(price) && price >= 1 && price <= 1_000_000);
  const freeActivationDatesValid = /^\d{4}-\d{2}-\d{2}$/.test(freeActivationStartDate)
    && /^\d{4}-\d{2}-\d{2}$/.test(freeActivationEndDate)
    && freeActivationStartDate <= freeActivationEndDate;
  const beijingToday = new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const freeActivationStatus = !freeActivationEnabled
    ? 'closed'
    : beijingToday < freeActivationStartDate
      ? 'before'
      : beijingToday > freeActivationEndDate
        ? 'ended'
        : 'active';

  return (
    <div className="dashboard">
      <div className="dashboard-head">
        <div>
          <span className="eyebrow">{t('主控台')}</span>
          <h1>{t('共享控制中心')}</h1>
          <p className="muted">{t('房间')} {state?.roomId ? <code>{state.roomId.slice(0, 18)}…</code> : t('尚未开始')}</p>
        </div>
        <div className="head-actions">
          <span className={`status-dot ${sharing ? 'live' : 'offline'}`}>{sharing ? t('正在共享') : t('未共享')}</span>
          <button className="ghost" onClick={() => void loadState()}>{t('刷新')}</button>
          <button className="ghost danger-text" onClick={logout}>{t('退出登录')}</button>
        </div>
      </div>

      {notice && <div className="alert info">{notice}</div>}
      {error && <div className="alert error">{error}</div>}

      <section className="metric-grid">
        <article className="metric"><span>{t('在线听众')}</span><strong>{state?.participants.length ?? 0}<small> / {MAX_LISTENERS}</small></strong></article>
        <article className="metric"><span>{t('连接状态')}</span><strong className="metric-text">{connectionLabel(connectionState)}</strong></article>
        <article className="metric"><span>{t('本月参与者分钟')}</span><strong>{state?.usage.monthParticipantMinutes ?? 0}</strong><div className="meter"><i style={{ width: `${usagePercent}%` }} /></div></article>
        <article className="metric"><span>{t('未使用激活码')}</span><strong>{activeInviteCount}</strong></article>
      </section>

      <div className="dashboard-grid">
        <section className="panel share-panel">
          <div className="section-heading">
            <div><span className="eyebrow">{t('系统声音')}</span><h2>{anySharing ? t('正在向听众发布') : t('准备共享电脑声音')}</h2></div>
            <span className={`capture-badge ${captureState}`}>{captureLabel(captureState)}</span>
          </div>
          <p className="muted">{captureMode === 'system'
            ? t('点击开始后，请在共享选择器中选择“整个屏幕”，并勾选“分享系统音频”。YY 语音、腾讯会议等软件声音会一起发送，其他系统声音也会包含在内。')
            : isDesktopRuntime()
              ? t('EXE 中将打开网页版主控页面，请在外部浏览器中选择一个或多个 Chrome/Edge 标签页；电脑系统音频请在 EXE 中直接使用。')
              : t('点击开始后，请在共享选择器中选择一个正在播放声音的 Chrome/Edge 标签页，并勾选“分享标签页音频”。')}</p>
          <div className="capture-mode-grid" role="radiogroup" aria-label={t('共享音频来源')}>
            <label className={`capture-mode ${captureMode === 'system' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="capture-mode"
                value="system"
                checked={captureMode === 'system'}
                onChange={() => setCaptureMode('system')}
                disabled={anySharing || busy === 'start'}
              />
              <span><strong>{t('电脑系统音频')}</strong><small>{t('共享电脑全部播放声音，包含 YY 语音、腾讯会议等软件。')}</small></span>
            </label>
            <label className={`capture-mode ${captureMode === 'tab' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="capture-mode"
                value="tab"
                checked={captureMode === 'tab'}
                onChange={() => setCaptureMode('tab')}
                disabled={anySharing || busy === 'start'}
              />
              <span><strong>{t('Chrome 标签页音频')}</strong><small>{t('只共享你在选择器中指定的 Chrome/Edge 标签页声音。')}</small></span>
            </label>
          </div>
          {captureMode === 'tab' && (
            <label className="stream-match-select">{t('本窗口直播比赛')}
              <select value={selectedMatchId} onChange={(event) => setSelectedMatchId(event.target.value)}>
                <option value="">{t('请选择比赛')}</option>
                {selectableScheduleMatches.map((match) => <option key={match.id} value={match.id}>{localizeScheduleName(match.homeTeam)} VS {localizeScheduleName(match.awayTeam)} · {formatScheduleDateTime(match.startsAt)} · {scheduleBroadcastLabel(match.broadcastType)}</option>)}
              </select>
            </label>
          )}
          {!captureSupported && <div className="alert error">{t('当前浏览器不支持读取系统声音，请改用最新版 Chrome 或 Edge。')}</div>}
          <div className="action-row">
            {!anySharing ? (
              <button className="primary large" onClick={() => void startSharing()} disabled={busy === 'start' || !captureSupported || (captureMode === 'tab' && !selectedMatchId)}>
                {busy === 'start' ? t('正在连接...') : t('开始共享') + captureModeLabel}
              </button>
            ) : (
              <>
                {captureMode === 'tab' && (
                  <button className="primary large" onClick={() => void startSharing()} disabled={busy === 'start' || !selectedMatchId || ownedStreams.length >= 2}>
                    {busy === 'start' ? t('正在添加...') : t('添加另一个标签页直播')}
                  </button>
                )}
                <button className="danger large" onClick={() => void stopSharing()} disabled={busy === 'stop'}>{t('停止全部共享')}</button>
              </>
            )}
          </div>
          {ownedStreams.length > 0 && (
            <div className="owned-stream-list">
              {ownedStreams.map((stream) => (
                <div className="record" key={stream.sessionId}>
                  <div><strong>{stream.match ? `${localizeScheduleName(stream.match.homeTeam)} VS ${localizeScheduleName(stream.match.awayTeam)}` : t('Chrome 标签页直播')}</strong><small>{stream.match ? localizeScheduleName(stream.match.league) : ''} · {stream.match ? scheduleBroadcastLabel(stream.match.broadcastType) : t('标签页音频')}</small></div>
                  {stream.needsCapture ? (
                    <button className="mini" onClick={() => void restoreOwnedCapture(stream.sessionId)} disabled={busy === `restore:${stream.sessionId}`}>
                      {busy === `restore:${stream.sessionId}` ? t('恢复中...') : t('重新选择标签页')}
                    </button>
                  ) : (
                    <button className="mini danger-text" onClick={() => void stopOwnedStream(stream.sessionId)} disabled={busy === `stop:${stream.sessionId}`}>{t('停止本场')}</button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="signal-strip">
            <span className={anySharing ? 'pulse' : ''} />
            <div>
              <strong>{sharing ? t('正在共享') + captureModeLabel : ownedStreams.length ? t('正在共享') + t('Chrome 标签页直播') : t('等待开始')}</strong>
              <small>{ownedStreams.length
                ? t('已启动 {count} 场标签页直播。', { count: ownedStreams.length })
                : captureMode === 'system'
                  ? t('电脑系统与软件声音以 48 kHz 立体声发送')
                  : t('所选 Chrome 标签页声音以 48 kHz 立体声发送')}</small>
            </div>
          </div>
        </section>

        <section className="panel invite-panel">
          <div className="section-heading">
            <div><span className="eyebrow">{t('可连续生成')}</span><h2>{t('生成 6 位激活码')}</h2></div>
            <span className="muted">{t('有效')} {activeInviteCount} / {MAX_ACTIVE_INVITES}</span>
          </div>
          <div className="invite-settings">
            <label>{t('新激活码有效期')}
              <select value={inviteTtlSeconds} onChange={(event) => setInviteTtlSeconds(Number(event.target.value) as InviteTtlSeconds)}>
                {INVITE_TTL_OPTIONS.map((option) => (
                  <option key={option.seconds} value={option.seconds}>{t(option.label)}</option>
                ))}
              </select>
            </label>
            <button
              className="primary"
              onClick={() => void createInvite()}
              disabled={busy === 'invite' || activeInviteCount >= MAX_ACTIVE_INVITES}
            >
              {busy === 'invite' ? t('正在生成...') : t('生成激活码')}
            </button>
            <p>{sharing ? t('生成后立即可用。') : t('可提前生成；主控开始共享后，听众才能进入。')} {t('每个码只允许首个设备使用，旧码不会被新码自动吊销。')}</p>
            {activeInviteCount >= MAX_ACTIVE_INVITES && <div className="alert error">{t('未使用激活码已达 {count} 个，请先吊销或等待旧码过期。', { count: MAX_ACTIVE_INVITES })}</div>}
          </div>

          {generatedInvites.length ? (
            <div className="generated-code-list">
              {generatedInvites.map((invite) => {
                const remaining = remainingSeconds(invite.expiresAt, clockMs);
                const status = invite.status === 'active' && remaining <= 0 ? 'expired' : invite.status;
                const active = status === 'active';
                const ttlLabel = INVITE_TTL_OPTIONS.find((option) => option.seconds === invite.ttlSeconds)?.label ?? '';
                return (
                  <div className={`code-display generated-code-card ${status}`} key={invite.inviteId}>
                    <span>{active ? t('{label}有效 · 发给一位听众', { label: t(ttlLabel) }) : inviteStatus(status)}</span>
                    <strong>{invite.code}</strong>
                    <div className="countdown">
                      <i style={{ width: `${active ? Math.max(0, Math.min(100, (remaining / invite.ttlSeconds) * 100)) : 0}%` }} />
                    </div>
                    <small>{active ? t('{time}后失效 · 最多 3 台设备', { time: formatRemaining(remaining) }) : t('状态：{status}', { status: inviteStatus(status) })}</small>
                    <div className="action-row center">
                      {active && <button className="secondary" onClick={() => void navigator.clipboard.writeText(invite.code)}>{t('复制激活码')}</button>}
                      {active && <button className="ghost danger-text" onClick={() => void revokeInvite(invite.inviteId)}>{t('吊销')}</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state compact">{t('本次登录尚未显示验证码明文。已生成的旧码仍按有效期有效，可在下方记录中吊销。')}</div>
          )}
        </section>
      </div>

      <section className="panel schedule-panel">
        <div className="section-heading">
          <div><span className="eyebrow">{t('赛程管理')}</span><h2>{t('比赛安排')}</h2></div>
          <span className="muted">{t('开赛 2 小时后从听众端隐藏')}</span>
        </div>
        <div className="schedule-manager">
          <form className="schedule-form" onSubmit={(event) => void saveSchedule(event)}>
            <label>{t('比赛时间')}<input type="datetime-local" value={scheduleForm.startsAt} onChange={(event) => setScheduleForm({ ...scheduleForm, startsAt: event.target.value })} required /></label>
            <label>{t('赛事类型')}<select value={scheduleForm.broadcastType} onChange={(event) => setScheduleForm({ ...scheduleForm, broadcastType: event.target.value as ScheduleBroadcastType })}><option value="foreign">{t('外语播报')}</option><option value="chinese">{t('中文播报')}</option></select></label>
            <label>{t('联赛名称')}<SearchableInput value={scheduleForm.league} suggestions={scheduleSuggestions.leagues.map((item) => item.value)} onChange={(value) => setScheduleForm({ ...scheduleForm, league: value })} placeholder={t('搜索或输入联赛，例如：英')} /></label>
            <label>{t('主队')}<SearchableInput value={scheduleForm.homeTeam} suggestions={scheduleSuggestions.teams.map((item) => item.value)} onChange={(value) => setScheduleForm({ ...scheduleForm, homeTeam: value })} placeholder={t('搜索或输入队名，例如：曼')} /></label>
            <label>{t('客队')}<SearchableInput value={scheduleForm.awayTeam} suggestions={scheduleSuggestions.teams.map((item) => item.value)} onChange={(value) => setScheduleForm({ ...scheduleForm, awayTeam: value })} placeholder={t('搜索或输入队名，例如：阿')} /></label>
            <div className="action-row">
              <button className="primary" disabled={busy === 'schedule'}>{scheduleForm.id ? (busy === 'schedule' ? t('保存中...') : t('保存修改')) : (busy === 'schedule' ? t('添加中...') : t('添加赛程'))}</button>
              {scheduleForm.id && <button type="button" className="ghost" onClick={resetScheduleForm}>{t('取消编辑')}</button>}
            </div>
          </form>
          <div className="schedule-admin-list">
            {!scheduleMatches.length ? (
              <div className="empty-state compact">{t('暂无赛程，请添加比赛时间和对阵。')}</div>
            ) : scheduleMatches.map((match) => {
              const expired = clockMs > new Date(match.startsAt).getTime() + 2 * 60 * 60 * 1000;
              return (
                <div className={`schedule-admin-row ${expired ? 'past' : ''}`} key={match.id}>
                  <div className="schedule-admin-main">
                    <strong>{localizeScheduleName(match.homeTeam)} <span>VS</span> {localizeScheduleName(match.awayTeam)}</strong>
                    <small>{formatScheduleDateTime(match.startsAt)} · {localizeScheduleName(match.league)} · {scheduleBroadcastLabel(match.broadcastType)}</small>
                  </div>
                  <span className={`mini-status ${expired ? 'expired' : 'active'}`}>{expired ? t('已结束') : t('待展示')}</span>
                  <div className="compact-actions">
                    <button className="mini" onClick={() => editSchedule(match)}>{t('编辑')}</button>
                    <button className="mini danger-text" disabled={busy === `schedule-delete:${match.id}`} onClick={() => void deleteSchedule(match.id)}>{t('删除')}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="section-heading">
          <div><span className="eyebrow">{t('实时管理')}</span><h2>{t('在线听众')}</h2></div>
          <span className="muted">{t('状态每 3 秒同步')}</span>
        </div>
        {!state?.participants.length ? (
          <div className="empty-state compact">{t('当前没有听众在线。')}</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>{t('听众')}</th><th>{t('设备')}</th><th>{t('加入时间')}</th><th>{t('延迟')}</th><th>{t('状态')}</th><th>{t('管理')}</th></tr></thead>
              <tbody>
                {state.participants.map((participant) => (
                  <tr key={participant.sessionId}>
                    <td><strong>{participant.nickname}</strong><small>{participant.userId.slice(0, 12)}…</small></td>
                    <td><code>{participant.deviceIdShort}</code></td>
                    <td>{formatTime(participant.joinedAt)}</td>
                    <td>{participant.latencyMs != null ? `${participant.latencyMs} ms` : '--'}</td>
                    <td><span className={`mini-status ${participant.connectionState}`}>{connectionLabel(participant.connectionState)}</span></td>
                    <td>
                      <div className="compact-actions">
                        <button className="mini" onClick={() => void participantAction(participant.sessionId, participant.connectionState === 'paused' ? 'resume' : 'pause')}>
                          {participant.connectionState === 'paused' ? t('恢复') : t('暂停')}
                        </button>
                        <button className="mini" onClick={() => void participantAction(participant.sessionId, 'kick')}>{t('踢出')}</button>
                        <button className="mini danger-text" onClick={() => void participantAction(participant.sessionId, 'ban')}>{t('封禁')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel">
        <div className="section-heading">
          <div><span className="eyebrow">USDT {t('自助购买')}</span><h2>{t('购买订单与异常付款')}</h2></div>
          <span className="muted">TRON · TRC20</span>
        </div>
        <div className="record-list">
          {activationOrders.slice(0, 10).map((order) => (
            <div className="record" key={order.orderId}>
              <div>
                <strong>{order.payableAmountUsdt} USDT · {activationPlanLabel(order.ttlSeconds)}</strong>
                <small>
                  {order.deviceIdShort} · {formatTime(order.createdAt)}
                  {order.txId ? ` · ${order.txId.slice(0, 12)}…` : ''}
                  {order.activationCode ? ` · ${t('激活码记录')} ${order.activationCode}` : ''}
                </small>
              </div>
              <span className={`mini-status ${order.status}`}>{orderStatus(order.status)}</span>
            </div>
          ))}
          {!activationOrders.length && <div className="empty-state compact">{t('暂无自助购买订单。')}</div>}
        </div>
        {activationPaymentEvents.length > 0 && (
          <details className="payment-events">
            <summary>{t('异常到账 {count} 笔', { count: activationPaymentEvents.length })}</summary>
            {activationPaymentEvents.slice(0, 10).map((event) => (
              <div className="record" key={event.txId}>
                <div><strong>{event.amountUsdt} USDT</strong><small>{event.fromAddress} · {formatTime(event.createdAt)} · {paymentReasonLabel(event.reason)}</small></div>
                <code title={event.txId}>{event.txId.slice(0, 12)}…</code>
              </div>
            ))}
          </details>
        )}
      </section>
      <section className="panel activation-code-manager">
        <div className="section-heading">
          <div><span className="eyebrow">{t('激活码管理')}</span><h2>{t('有效激活码')}</h2></div>
          <span className="muted">{t('过期与吊销记录自动隐藏')}</span>
        </div>
        <div className="activation-code-toolbar">
          <div className="activation-code-tabs" role="tablist">
            {(['owner', 'purchase', 'free'] as const).map((source) => (
              <button
                type="button"
                role="tab"
                aria-selected={activationCodeSource === source}
                className={activationCodeSource === source ? 'active' : ''}
                key={source}
                onClick={() => setActivationCodeSource(source)}
              >
                {activationSourceLabel(source)} <strong>{activationCodeCounts[source]}</strong>
              </button>
            ))}
          </div>
          <input value={activationCodeQuery} onChange={(event) => setActivationCodeQuery(event.target.value)} placeholder={t('搜索激活码、昵称或设备')} />
        </div>
        <div className="activation-code-list">
          {managedActivationCodes.map((invite) => {
            const remaining = remainingSeconds(invite.expiresAt, clockMs);
            const urgency = remaining <= 3600 ? 'urgent' : remaining <= 86400 ? 'soon' : '';
            const order = invite.orderId ? activationOrders.find((item) => item.orderId === invite.orderId) : undefined;
            return (
              <div className="activation-code-row" key={invite.inviteId}>
                <div className="activation-code-main">
                  <strong>{invite.code ?? t('未显示')}</strong>
                  <small>
                    {invite.nickname ?? t('尚未使用')} · {t('设备')} {invite.deviceCount}/{invite.deviceLimit}
                    {invite.activeDeviceIdShort ? ` · ${t('当前')} ${invite.activeDeviceIdShort}` : ''}
                  </small>
                  {invite.source === 'purchase' && (
                    <small>{order ? `${order.payableAmountUsdt} USDT · ${t('订单')} ${order.orderId.slice(0, 8)}${order.txId ? ` · ${order.txId.slice(0, 12)}…` : ''}` : `${t('订单')} ${invite.orderId?.slice(0, 8) ?? '--'}`}</small>
                  )}
                </div>
                <span className={`activation-code-countdown ${urgency}`}>{formatRemaining(remaining)}</span>
                <div className="activation-code-actions">
                  {invite.code && <button className="mini" type="button" onClick={() => void navigator.clipboard.writeText(invite.code!)}>{t('复制')}</button>}
                  <button className="mini danger-text" type="button" onClick={() => void revokeInvite(invite.inviteId)}>{t('吊销')}</button>
                </div>
              </div>
            );
          })}
          {!managedActivationCodes.length && <div className="empty-state compact">{t('当前分组没有有效激活码。')}</div>}
        </div>
      </section>

      <div className="dashboard-grid lower-grid">
        <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">{t('费用保护')}</span><h2>{t('月度使用上限')}</h2></div></div>
          <div className="inline-form">
            <input type="number" min={0} value={usageLimit} onChange={(event) => { usageDirtyRef.current = true; setUsageLimit(Number(event.target.value)); }} />
            <span>{t('参与者分钟')}</span>
            <button className="secondary" onClick={() => void saveUsageLimit()} disabled={busy === 'usage'}>{t('保存')}</button>
          </div>
          <p className="muted">{t('默认 9,000 分钟，为腾讯云免费额度预留余量；设为 0 表示不限制。达到上限后新听众会被拒绝，已有听众不会被强制中断。')}</p>
        </section>

        <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">{t('显示效果')}</span><h2>{t('虚拟在线人数')}</h2></div></div>
          <div className="inline-form">
            <input type="number" min={0} max={1000000} value={virtualOnlineBase} onChange={(event) => { virtualOnlineDirtyRef.current = true; setVirtualOnlineBase(Number(event.target.value)); }} />
            <span>{t('基础人数')}</span>
            <button className="secondary" onClick={() => void saveVirtualOnlineBase()} disabled={busy === 'virtual-online'}>{t('保存')}</button>
          </div>
          <p className="muted">{t('当前显示 {count} 人；保存后立即生效并保持到下一次 5–10 分钟更新；开播后按设定基数波动约 ±1；无直播且超过该窗口后恢复随机 5–10 人；真人加入或退出会同步 +1 / -1。该数字不影响真实听众上限。', { count: state?.displaySettings.virtualOnlineCount ?? virtualOnlineBase })}</p>

          <div className="display-settings-divider" />
          <div className="section-heading compact-heading"><div><span className="eyebrow">{t('听众通知')}</span><h2>{t('滚动字幕')}</h2></div></div>
          <div className="scrolling-message-editor">
            <input
              type="text"
              maxLength={200}
              value={scrollingMessage}
              onChange={(event) => {
                scrollingMessageDirtyRef.current = true;
                setScrollingMessage(event.target.value);
              }}
              placeholder={t('输入要滚动显示的通知内容')}
            />
            <span>{scrollingMessage.length}/200</span>
            <button className="secondary" onClick={() => void saveScrollingMessage()} disabled={busy === 'scrolling-message'}>{t('保存字幕')}</button>
            <button className="ghost" onClick={() => { setScrollingMessage(''); scrollingMessageDirtyRef.current = true; void saveScrollingMessage(''); }} disabled={busy === 'scrolling-message'}>{t('清空')}</button>
          </div>
          <p className="muted">{t('听众端登录前和进入语音室后都会在赛程卡下方显示。留空并保存即可隐藏。')}</p>
        </section>
      </div>

      <section className="panel">
        <div className="section-heading">
          <div><span className="eyebrow">{t('收费设置')}</span><h2>{t('激活码价格与收费模式')}</h2></div>
          <span className={paidActivationEnabled ? 'active-status' : 'inactive-status'}>{paidActivationEnabled ? t('收费模式已开启') : t('收费模式已关闭')}</span>
        </div>
        <div className="price-settings-grid">
          <label>{t('日卡价格')}<input type="number" min={1} max={1000000} step={1} value={activationPriceDay} onChange={(event) => { activationPricesDirtyRef.current = true; setActivationPriceDay(Number(event.target.value)); }} /><span>USDT</span></label>
          <label>{t('周卡价格')}<input type="number" min={1} max={1000000} step={1} value={activationPriceWeek} onChange={(event) => { activationPricesDirtyRef.current = true; setActivationPriceWeek(Number(event.target.value)); }} /><span>USDT</span></label>
          <label>{t('月卡价格')}<input type="number" min={1} max={1000000} step={1} value={activationPriceMonth} onChange={(event) => { activationPricesDirtyRef.current = true; setActivationPriceMonth(Number(event.target.value)); }} /><span>USDT</span></label>
        </div>
        <div className="action-row">
          <button className="secondary" disabled={busy === 'activation-prices' || !pricesValid} onClick={() => void saveActivationPrices()}>{t('保存价格')}</button>
          <button className={paidActivationEnabled ? 'ghost danger-text' : 'primary'} disabled={busy === 'paid-activation'} onClick={() => void togglePaidActivation()}>{paidActivationEnabled ? t('关闭收费模式') : t('开启收费模式')}</button>
        </div>
        <p className="muted">{t('关闭收费模式后，听众端会隐藏付费购买区域，但已有订单仍可继续支付并自动发码；免费领取不受影响。价格仅支持 1–1,000,000 的整数 USDT，修改只影响新订单。')}</p>
      </section>

      <section className="panel">
        <div className="section-heading">
          <div><span className="eyebrow">{t('免费活动')}</span><h2>{t('免费激活码活动')}</h2></div>
          <span className={freeActivationStatus === 'active' ? 'active-status' : 'inactive-status'}>
            {freeActivationStatus === 'active' ? t('进行中') : freeActivationStatus === 'before' ? t('未开始') : freeActivationStatus === 'ended' ? t('已结束') : t('已关闭')}
          </span>
        </div>
        <div className="activity-date-grid">
          <label>{t('开始日期')}<input type="date" value={freeActivationStartDate} onChange={(event) => { freeActivationDirtyRef.current = true; setFreeActivationStartDate(event.target.value); }} /></label>
          <label>{t('结束日期')}<input type="date" value={freeActivationEndDate} onChange={(event) => { freeActivationDirtyRef.current = true; setFreeActivationEndDate(event.target.value); }} /></label>
        </div>
        <div className="action-row">
          <button className="secondary" disabled={busy === 'free-activation-dates' || !freeActivationDatesValid} onClick={() => void saveFreeActivationSchedule()}>{t('保存活动时间')}</button>
          <button className={freeActivationEnabled ? 'ghost danger-text' : 'primary'} disabled={busy === 'free-activation-mode'} onClick={() => void toggleFreeActivation()}>{freeActivationEnabled ? t('关闭免费活动') : t('开启免费活动')}</button>
        </div>
        <p className="muted">{t('开始日和结束日均按北京时间计算。每台设备每天可领取一次，不限制IP；关闭活动或日期不可用时隐藏领取入口，但已领取且未过期的免费码仍可使用。')}</p>
      </section>

      <section className="panel">
        <div className="section-heading">
          <div><span className="eyebrow">{t('安全')}</span><h2>{t('封禁设备')}</h2></div>
          <button className="ghost" onClick={() => setShowAudit((value) => !value)}>{showAudit ? t('隐藏审计日志') : t('查看审计日志')}</button>
        </div>
        {bans.filter((ban) => ban.active).length ? bans.filter((ban) => ban.active).map((ban) => (
          <div className="record" key={ban.deviceId}>
            <div>
              <strong title={ban.reason}>{ban.subjectType === 'ip' ? `IP ${ban.displayValue ?? ban.deviceId.slice(0, 12)}` : (ban.displayValue ?? ban.deviceId.slice(0, 12))}</strong>
              <small>{ban.banKind === 'payment' ? t('付款超时封禁') : t('设备封禁')} · {formatTime(ban.createdAt)}</small>
            </div>
            <button className="mini" onClick={() => void unban(ban.deviceId)}>{t('解除封禁')}</button>
          </div>
        )) : <div className="empty-state compact">{t('当前没有封禁设备。')}</div>}
        {showAudit && (
          <div className="audit-list">
            {audit.map((event) => (
              <div key={event.id}><code>{event.action}</code><span>{formatTime(event.at)}</span><small>{event.target ?? ''}</small></div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function remainingSeconds(expiresAt: string, nowMs: number): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - nowMs) / 1000));
}

function formatRemaining(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const remainingSeconds = seconds % 60;
  if (days > 0) return t('{days}天 {clock}', { days, clock: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}` });
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
}

function connectionLabel(value: string): string {
  return ({ connected: t('已连接'), connecting: t('重连中'), disconnected: t('未连接'), unstable: t('网络不稳定'), resuming: t('恢复中'), paused: t('已暂停') } as Record<string, string>)[value] ?? value;
}

function captureLabel(value: string): string {
  return ({ idle: t('等待开始'), requesting: t('等待授权'), live: t('音频已就绪'), error: t('授权失败') } as Record<string, string>)[value] ?? value;
}

function activationPlanLabel(ttlSeconds: number): string {
  return ({ 86_400: t('1 天'), 604_800: t('1 周'), 2_592_000: t('1 个月') } as Record<number, string>)[ttlSeconds] ?? t('{seconds} 秒', { seconds: ttlSeconds });
}

function orderStatus(value: string): string {
  return ({ pending: t('待支付'), paid: t('已支付'), expired: t('已过期') } as Record<string, string>)[value] ?? value;
}

function paymentReasonLabel(value: string): string {
  return ({ unsupported_token: t('非指定 USDT'), amount_or_order_not_found: t('金额或订单不匹配'), outside_payment_window: t('不在付款时限'), already_processed: t('交易已处理') } as Record<string, string>)[value] ?? value;
}
function scheduleBroadcastLabel(value: string): string {
  return value === 'chinese' ? t('中文播报') : t('外语播报');
}

function activationSourceLabel(source: 'owner' | 'purchase' | 'free'): string {
  return ({ owner: t('主控生成'), purchase: t('购买激活码'), free: t('免费领取') } as const)[source];
}
function inviteStatus(value: string): string {
  return ({ active: t('有效'), redeemed: t('已启用'), revoked: t('已吊销'), expired: t('已过期') } as Record<string, string>)[value] ?? value;
}

function SearchableInput({ value, suggestions, onChange, placeholder }: { value: string; suggestions: string[]; onChange: (value: string) => void; placeholder: string }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const query = normalizeSearch(value);
  const matches = suggestions
    .filter((suggestion) => !query || normalizeSearch(suggestion).includes(query))
    .slice(0, 8);

  function selectSuggestion(suggestion: string): void {
    onChange(suggestion);
    setOpen(false);
    setActiveIndex(0);
  }

  return (
    <div className="searchable-input">
      <input value={value} maxLength={40} required autoComplete="off" placeholder={placeholder} onFocus={() => setOpen(true)} onBlur={() => window.setTimeout(() => setOpen(false), 120)} onChange={(event) => { onChange(event.target.value); setOpen(true); setActiveIndex(0); }} onKeyDown={(event) => {
        if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); setActiveIndex((index) => Math.min(index + 1, Math.max(0, matches.length - 1))); }
        else if (event.key === 'ArrowUp') { event.preventDefault(); setActiveIndex((index) => Math.max(0, index - 1)); }
        else if (event.key === 'Enter' && open && matches[activeIndex]) { event.preventDefault(); selectSuggestion(matches[activeIndex]); }
        else if (event.key === 'Escape') setOpen(false);
      }} />
      {open && matches.length > 0 && <div className="autocomplete-menu" role="listbox">{matches.map((suggestion, index) => <button type="button" role="option" aria-selected={index === activeIndex} className={index === activeIndex ? 'active' : ''} key={suggestion} onMouseDown={(event) => event.preventDefault()} onClick={() => selectSuggestion(suggestion)}><span>{suggestion}</span><small>{query && normalizeSearch(suggestion).includes(query) ? t('匹配') : t('常用')}</small></button>)}</div>}
    </div>
  );
}

function normalizeSearch(value: string): string { return value.normalize('NFKC').trim().toLowerCase(); }
function formatScheduleDateTime(value: string): string {
  return new Intl.DateTimeFormat(getLanguage() === 'zh' ? 'zh-CN' : getLanguage() === 'ru' ? 'ru-RU' : 'en-US', {
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getLanguageTimeZone()
  }).format(new Date(value));
}
function toDateTimeLocal(value: string): string {
  const date = new Date(value);
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
function formatTime(value: string): string {
  return new Intl.DateTimeFormat(getLanguage() === 'zh' ? 'zh-CN' : getLanguage() === 'ru' ? 'ru-RU' : 'en-US', { hour: '2-digit', minute: '2-digit', month: '2-digit', day: '2-digit', timeZone: getLanguageTimeZone() }).format(new Date(value));
}

function messageOf(cause: unknown): string {
  if (cause instanceof ApiError) return tError(cause.code, cause.message);
  return cause instanceof Error ? cause.message : String(cause);
}






